import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

/**
 * TermsPage - Terms of Use page
 * Migrated from public/terms.html with all original content preserved
 */
export function TermsPage() {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">Terms of Use</h1>
            <p className="text-xl text-slate-200">Last updated: January {currentYear}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Important Warning */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-3">
                <div className="text-red-600 text-2xl">⚠️</div>
                <div>
                  <h3 className="text-red-800 font-bold text-lg mb-2">IMPORTANT: Educational Tool Only</h3>
                  <p className="text-red-700 font-semibold">
                    FinModel is a FREE educational financial modeling tool. By using this service, you
                    acknowledge that:
                  </p>
                  <ul className="mt-3 space-y-2 text-red-700">
                    <li>• You are using this tool at your own risk</li>
                    <li>• You are responsible for verifying all calculations</li>
                    <li>• This is NOT professional financial advice</li>
                    <li>• You must consult qualified professionals for business decisions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
              <p className="mb-6 text-slate-600">
                By accessing and using FinModel ("the Service"), you accept and agree to be bound by the terms
                and provision of this agreement. If you do not agree to abide by the above, please do not use
                this service.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Educational Purpose</h2>
              <p className="mb-6 text-slate-600">
                FinModel is designed as an educational tool to help users understand financial modeling
                concepts. The models, calculations, and outputs provided are for learning purposes only and
                should not be used as the sole basis for business or investment decisions.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. No Warranty</h2>
              <p className="mb-6 text-slate-600">
                The information and calculations provided by FinModel are offered "as is" without any warranty.
                We make no representations or warranties of any kind, express or implied, about the
                completeness, accuracy, reliability, suitability, or availability of the information, products,
                services, or related graphics contained in the Service.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. User Responsibility</h2>
              <p className="mb-4 text-slate-600">You are solely responsible for:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>Verifying all calculations and assumptions</li>
                <li>Consulting with qualified financial professionals</li>
                <li>Ensuring compliance with applicable laws and regulations</li>
                <li>Any business decisions made based on the models</li>
                <li>The accuracy of data you input into the system</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Limitation of Liability</h2>
              <p className="mb-6 text-slate-600">
                In no event shall FinModel, its creators, or contributors be liable for any direct, indirect,
                incidental, special, or consequential damages arising out of the use or inability to use the
                Service, including but not limited to damages for loss of profits, business interruption, or
                loss of information.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data and Privacy</h2>
              <p className="mb-6 text-slate-600">
                We do not store, collect, or transmit your financial data. All calculations are performed
                locally in your browser. However, we recommend reviewing our Privacy Policy for complete details
                about data handling.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Prohibited Uses</h2>
              <p className="mb-4 text-slate-600">You may not use FinModel for:</p>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>Any unlawful purpose or activity</li>
                <li>Commercial redistribution without permission</li>
                <li>Attempting to reverse engineer the calculations</li>
                <li>Overloading or attempting to disable the Service</li>
                <li>Violating any applicable laws or regulations</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Professional Advice Requirement</h2>
              <p className="mb-6 text-slate-600">
                <strong>YOU MUST CONSULT QUALIFIED PROFESSIONALS:</strong> Before making any business,
                financial, or investment decisions, you must consult with qualified accountants, financial
                advisors, lawyers, or other appropriate professionals. FinModel is not a substitute for
                professional advice.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Modifications to Terms</h2>
              <p className="mb-6 text-slate-600">
                We reserve the right to modify these terms at any time. Continued use of the Service after
                changes constitutes acceptance of the new terms.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Termination</h2>
              <p className="mb-6 text-slate-600">
                We may terminate or suspend access to the Service immediately, without prior notice, for any
                reason, including breach of these Terms.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Governing Law</h2>
              <p className="mb-6 text-slate-600">
                These Terms shall be governed by and construed in accordance with applicable laws. Any disputes
                arising under these terms shall be resolved in appropriate jurisdictions.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Information</h2>
              <p className="mb-6 text-slate-600">
                If you have any questions about these Terms of Use, please contact us at:{' '}
                <a href="mailto:hello@finmodel.app" className="text-primary-600 hover:underline">
                  hello@finmodel.app
                </a>
              </p>
            </div>

            {/* Summary */}
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <h3 className="text-primary-800 font-semibold mb-2">Summary</h3>
                <p className="text-primary-700 text-sm">
                  FinModel is a free educational tool provided "as is" for learning purposes.
                  You are responsible for verifying all outputs and must consult qualified professionals
                  before making any business decisions. Use at your own risk.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
